import {undef} from './undef';
import {getNoUndefValuesArray} from './array';
import {getQuantityLabel} from './string';
import {allowFieldAutoSubmit, allowInputFieldErrorHighlighting} from './field';
import {runGuestPickerCTASearchExperiment} from './experiments';
import {shouldGuestsFieldAutoSubmitOnCloseOnHP} from './form';

// eslint-disable-next-line complexity
function getGuestsFieldInputLabel(guestsTranslations, guestsValues) {
    const {
        adultsCount,
        childrenCount
    } = guestsValues;

    const guestsCount = adultsCount + childrenCount;

    const {
        separatorText,
        guestText,
        guestsText,
        petsText,
        onlyPetsText
    } = guestsTranslations;

    const {
        petIncluded
    } = guestsValues;

    const petsTranslation = (guestsCount) ? petsText : onlyPetsText;

    return getNoUndefValuesArray([
        guestsCount ? getQuantityLabel({
            count: guestsCount,
            singular: guestText,
            plural: guestsText
        }) : undef,
        petIncluded ? petsTranslation : undef
    ]).join(separatorText);
}

function getGuestsFieldAdultsInputLabel(guestsTranslations, guestsValues, locale) {
    const {
        adultText,
        adultsText,
        zeroAdultsText
    } = guestsTranslations;

    const {
        adultsCount
    } = guestsValues;

    return getQuantityLabel({
        count: adultsCount,
        singular: adultText,
        plural: adultsText,
        locale,
        zero: zeroAdultsText
    });
}

function getGuestsFieldChildrenInputLabel(guestsTranslations, guestsValues, locale) {
    const {
        childText,
        childrenText,
        zeroChildrenText
    } = guestsTranslations;

    const {
        childrenCount
    } = guestsValues;

    return getQuantityLabel({
        count: childrenCount,
        singular: childText,
        plural: childrenText,
        locale,
        zero: zeroChildrenText
    });
}

function getGuestsFieldAgeSelectTitle(guestsTranslations, guestsValues) {
    const {
        ageSelectTitle,
        ageSelectTitleSingular
    } = guestsTranslations;

    const {
        childrenCount
    } = guestsValues;

    return childrenCount > 1 ? ageSelectTitle : ageSelectTitleSingular;
}

function hasEmptyGuestsCount(guestsCount) {
    return !guestsCount;
}

// eslint-disable-next-line no-unused-vars
function hasEmptyGuests(adultsCount, childrenCount, petIncluded) {
    return hasEmptyGuestsCount(adultsCount);
}

function hasEmptyGuestsValues({adultsCount, childrenCount, petIncluded}) {
    return hasEmptyGuests(adultsCount, childrenCount, petIncluded);
}

function hasFilledGuestsCount(guestsCount) {
    return !hasEmptyGuestsCount(guestsCount);
}

/* istanbul ignore next */
// eslint-disable-next-line no-unused-vars
function hasFilledGuests(adultsCount, childrenCount, petIncluded) {
    return hasFilledGuestsCount(adultsCount);
}

/* istanbul ignore next */
function hasFilledGuestsValues({adultsCount, childrenCount, petIncluded}) {
    return hasFilledGuests(adultsCount, childrenCount, petIncluded);
}

function hasValidGuestsCount(guestsCount) {
    return hasFilledGuestsCount(guestsCount);
}

// eslint-disable-next-line no-unused-vars
function hasValidGuests(adultsCount, childrenCount, petIncluded) {
    return hasValidGuestsCount(adultsCount);
}

function hasValidGuestsValues({adultsCount, childrenCount, petIncluded}) {
    return hasValidGuests(adultsCount, childrenCount, petIncluded);
}

function shouldChildrenAgesCookieUpdate(actionLocation) {
    return actionLocation === 'search';
}

function shouldGuestsFieldAutoSubmitOnClose(mediaSettings, formOptions, guestsOptions, abacusStore, systemOptions, latestDestinationValues, latestDatesValues) {
    const {
        isMobile
    } = mediaSettings;

    return (
        isMobile && allowFieldAutoSubmit(formOptions, guestsOptions) ||
        runGuestPickerCTASearchExperiment({shouldLogExperiment: true}, abacusStore, latestDestinationValues, latestDatesValues) || shouldGuestsFieldAutoSubmitOnCloseOnHP(systemOptions, latestDestinationValues, latestDatesValues)
    );
}

function getAddedChildAgeData(ageOptions) {
    const {minAge, useUndefAges, undefAgesValue} = ageOptions;
    if (typeof minAge !== 'undefined') {
        return {
            value: minAge,
            label: `${minAge}`,
        };
    }
    if (useUndefAges && undefAgesValue) {
        return {
            ...undefAgesValue,
        };
    }
    return {
        value: 0,
        label: '0',
    };
}

function setChildrenAgeSelectorData(childrenCount, props) {
    const {prevValue, nextValue} = childrenCount;
    let ageData = props.ageData;
    ageData =
        prevValue < nextValue
            ? [
                ...ageData,
                getAddedChildAgeData(props.ageOptions)
            ]
            : ageData.slice(0, nextValue);
    return ageData;
}

function showGuestsInputFieldError(mediaSettings, formOptions, guestsOptions, latestGuestsValues) {
    const {
        isMobile
    } = mediaSettings;

    return !isMobile && (
        allowInputFieldErrorHighlighting(formOptions, guestsOptions) && !hasValidGuestsValues(latestGuestsValues)
    );
}

function showGuestsInputIcon(formOptions, guestsOptions) {
    const {
        showIcons
    } = formOptions;

    const {
        showInputIcon
    } = guestsOptions;

    return (
        showInputIcon ||
        showIcons && showInputIcon === undef
    );
}

export {
    getAddedChildAgeData,
    getGuestsFieldInputLabel,
    getGuestsFieldAdultsInputLabel,
    getGuestsFieldAgeSelectTitle,
    getGuestsFieldChildrenInputLabel,
    hasEmptyGuests,
    hasEmptyGuestsCount,
    hasEmptyGuestsValues,
    hasFilledGuests,
    hasFilledGuestsCount,
    hasFilledGuestsValues,
    hasValidGuests,
    hasValidGuestsCount,
    hasValidGuestsValues,
    shouldChildrenAgesCookieUpdate,
    shouldGuestsFieldAutoSubmitOnClose,
    setChildrenAgeSelectorData,
    showGuestsInputFieldError,
    showGuestsInputIcon
};
